import React from 'react';
import { Calendar, X } from 'lucide-react';
import { dateToDateTimeLocalKST } from '../../utils/kstDateTime';

/**
 * Parse a datetime-local style string ("YYYY-MM-DDTHH:MM") into a Date.
 * Returns null for empty / invalid input.
 */
function parseLocalString(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format a Date as "YYYY-MM-DDTHH:MM" (matching native datetime-local). */
function formatLocalString(date) {
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

const KST_TIME_ZONE = 'Asia/Seoul';

/**
 * DateTimePicker — wrapper around native datetime-local input that speaks the
 * same "YYYY-MM-DDTHH:MM" (datetime-local) string format as the rest of the
 * admin forms, so it can be used as a drop-in replacement.
 *
 * When `timeZone="Asia/Seoul"`, the value is treated as KST wall clock (no browser TZ reinterpretation).
 *
 * @param {{ value?: string, onChange: (next: string) => void, placeholder?: string, clearable?: boolean, disabled?: boolean, required?: boolean, minDate?: Date, timeZone?: string }} props
 */
export default function DateTimePicker({
  value,
  onChange,
  placeholder,
  clearable = true,
  disabled = false,
  required = false,
  minDate,
  timeZone,
}) {
  const useKst = timeZone === KST_TIME_ZONE;

  let inputValue;
  if (useKst) {
    inputValue = value ?? '';
  } else {
    const selected = parseLocalString(value);
    inputValue = selected ? formatLocalString(selected) : '';
  }

  let minValue;
  if (useKst) {
    minValue = minDate ? dateToDateTimeLocalKST(minDate) : undefined;
  } else {
    minValue = minDate ? formatLocalString(minDate) : undefined;
  }

  return (
    <div className="w-full flex items-center gap-2 bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-sm text-dark-text">
      <Calendar className="w-4 h-4 text-white flex-shrink-0" />
      <input
        type="datetime-local"
        value={inputValue}
        onChange={(e) => onChange(e.target.value)}
        min={minValue}
        disabled={disabled}
        required={required}
        placeholder={placeholder || 'YYYY-MM-DDTHH:MM'}
        className="datetime-local-input flex-1 bg-transparent outline-none text-dark-text placeholder-dark-text-muted"
      />
      {clearable && !required && value && !disabled && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="p-0.5 rounded hover:bg-dark-border text-dark-text-secondary hover:text-primary"
          aria-label="clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
